import { ReactNode } from 'react';

/**
 * The little markdown the assistant writes — paragraphs, headings, lists,
 * code fences, `code`, **bold** and *emphasis* — drawn as React elements.
 * Never as HTML: the text came from a model.
 */

function inline(text: string, key: string): ReactNode[] {
  const nodes: ReactNode[] = [];
  const pattern = /(`[^`]+`|\*\*[^*]+\*\*|\*[^*\s][^*]*\*)/g;
  let last = 0;
  let i = 0;
  for (const match of text.matchAll(pattern)) {
    if ((match.index ?? 0) > last) nodes.push(text.slice(last, match.index));
    const token = match[0];
    const k = `${key}-${i++}`;
    if (token.startsWith('`')) nodes.push(<code key={k} className="rounded-sm bg-muted px-1 py-px font-mono text-[0.8em]">{token.slice(1, -1)}</code>);
    else if (token.startsWith('**')) nodes.push(<strong key={k}>{token.slice(2, -2)}</strong>);
    else nodes.push(<em key={k}>{token.slice(1, -1)}</em>);
    last = (match.index ?? 0) + token.length;
  }
  if (last < text.length) nodes.push(text.slice(last));
  return nodes;
}

export function AssistantMarkdown({ text }: { text: string }) {
  const out: ReactNode[] = [];
  const lines = text.replace(/\r\n/g, '\n').split('\n');
  let i = 0;
  let key = 0;
  while (i < lines.length) {
    const line = lines[i];
    const fence = /^```(\w*)/.exec(line.trim());
    if (fence) {
      const code: string[] = [];
      i += 1;
      while (i < lines.length && !lines[i].trim().startsWith('```')) code.push(lines[i++]);
      i += 1;
      out.push(<pre key={key++} className="overflow-auto rounded-md border-[1px] bg-muted/30 px-2 py-1.5 font-mono text-[0.7rem] leading-relaxed"><code>{code.join('\n')}</code></pre>);
      continue;
    }
    const heading = /^(#{1,4})\s+(.*)$/.exec(line);
    if (heading) {
      out.push(<p key={key++} className="pt-1 font-semibold">{inline(heading[2], `h${key}`)}</p>);
      i += 1;
      continue;
    }
    if (/^\s*([-*]|\d+\.)\s+/.test(line)) {
      const ordered = /^\s*\d+\./.test(line);
      const items: string[] = [];
      while (i < lines.length && /^\s*([-*]|\d+\.)\s+/.test(lines[i])) {
        let item = lines[i].replace(/^\s*([-*]|\d+\.)\s+/, '');
        i += 1;
        // a wrapped item continues on indented lines
        while (i < lines.length && /^\s{2,}\S/.test(lines[i]) && !/^\s*([-*]|\d+\.)\s+/.test(lines[i])) item += ` ${lines[i++].trim()}`;
        items.push(item);
      }
      const children = items.map((item, n) => <li key={n}>{inline(item, `l${key}-${n}`)}</li>);
      out.push(ordered ? <ol key={key++} className="list-decimal space-y-0.5 pl-5">{children}</ol> : <ul key={key++} className="list-disc space-y-0.5 pl-5">{children}</ul>);
      continue;
    }
    if (!line.trim()) {
      i += 1;
      continue;
    }
    const para: string[] = [];
    while (i < lines.length && lines[i].trim() && !/^```/.test(lines[i].trim()) && !/^#{1,4}\s/.test(lines[i]) && !/^\s*([-*]|\d+\.)\s+/.test(lines[i])) para.push(lines[i++]);
    out.push(<p key={key++} className="leading-relaxed">{inline(para.join(' '), `p${key}`)}</p>);
  }
  return <div className="space-y-2">{out}</div>;
}
